import React, { useEffect, useState } from 'react';
import { Modal, Button, StatusBanner } from '../ui';
import { Textarea } from '../ui/FormField';
import { BookingListItem } from '../../types/booking';
import { formatDeadline, formatGraceDeadline, isWithinGrace } from '../../constants/policy';
import { usePolicy } from '../../contexts/PolicyContext';

interface CancelPayload {
  reason: string;
  internal_note?: string;
  release_room: boolean;
}

interface CancelBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingListItem | null;
  /** Inside the cancellation window? Computed from the client clock — a hint only. The server re-checks. */
  isWithinWindow: boolean;
  onConfirm: (payload: CancelPayload) => Promise<void>;
  isSubmitting: boolean;
  /** Surfaces a server-side downgrade (boundary crossed) or other error. */
  serverError?: string | null;
}

const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });

/**
 * Clinician cancel dialog. Two modes, chosen by the booking itself:
 * - client appointment: reason is required (sent to the client); outside the
 *   window the clinician chooses to release or keep the room.
 * - own room booking (self-booking / pod): reason optional; outside the window
 *   the room is simply released, inside it the cancel is allowed but billed.
 */
const CancelBookingModal: React.FC<CancelBookingModalProps> = ({
  isOpen, onClose, booking, isWithinWindow, onConfirm, isSubmitting, serverError,
}) => {
  const { windowHours: CANCELLATION_WINDOW_HOURS, graceMinutes: BOOKING_GRACE_MINUTES } = usePolicy();
  const [reason, setReason] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [releaseRoom, setReleaseRoom] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setInternalNote('');
      setReleaseRoom(true);
    }
  }, [isOpen]);

  if (!booking) return null;

  const isSelf = !!booking.is_self_booked;
  const hasGuestClient = isSelf && !!booking.client_email;
  const inGrace = isWithinGrace(booking.created_at, new Date(), BOOKING_GRACE_MINUTES);
  const reasonValid = isSelf ? true : reason.trim().length >= 3;
  const effectiveRelease = isWithinWindow ? false : (isSelf ? true : releaseRoom);

  const handleConfirm = () => {
    if (!reasonValid) return;
    onConfirm({
      reason: reason.trim(),
      internal_note: internalNote.trim() || undefined,
      release_room: effectiveRelease,
    });
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
        {isSelf ? 'Keep booking' : 'Keep appointment'}
      </Button>
      <Button
        variant="danger"
        onClick={handleConfirm}
        isLoading={isSubmitting}
        disabled={!reasonValid || isSubmitting}
      >
        {isSelf ? 'Cancel booking' : 'Cancel appointment'}
      </Button>
    </div>
  );

  const who = isSelf ? (booking.client_name ? `Your booking with ${booking.client_name}` : 'Your room booking') : (booking.patient_name ?? 'Client');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isSelf ? 'Cancel room booking' : 'Cancel appointment'}
      description={`${who} · ${formatWhen(booking.start_time)} · ${booking.duration_minutes} min`}
      size="md"
      footer={footer}
    >
      <div className="space-y-4">
        {serverError && (
          <StatusBanner variant="warning" title="Room release no longer possible" message={serverError} />
        )}

        {inGrace && (
          <StatusBanner
            variant="info"
            title="Made a mistake? No charge."
            message={`This booking was made less than ${BOOKING_GRACE_MINUTES} minutes ago, so cancelling now releases the room at no charge (until ${formatGraceDeadline(booking.created_at as string, BOOKING_GRACE_MINUTES)}).`}
          />
        )}
        {isWithinWindow && !inGrace ? (
          <StatusBanner
            variant="warning"
            title={`Within ${CANCELLATION_WINDOW_HOURS} hours`}
            message="You can still cancel, but the room stays reserved and you will be billed for this slot. If this is an emergency, contact admin to request a credit."
          />
        ) : isSelf ? (
          inGrace ? null :
          <StatusBanner
            variant="info"
            title="The room will be released"
            message={`You won't be billed for this slot. Free cancellation until ${formatDeadline(booking.start_time, CANCELLATION_WINDOW_HOURS)}.`}
          />
        ) : (
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-clinic-dark mb-1">
              What happens to your room time?
            </legend>
            <label className="flex items-start gap-3 p-3 rounded-card border border-clinic-primary-lighter cursor-pointer">
              <input
                type="radio"
                name="release_room"
                checked={releaseRoom}
                onChange={() => setReleaseRoom(true)}
                className="mt-1 accent-clinic-primary"
              />
              <span className="text-sm text-clinic-dark">
                <span className="font-medium">Release the room</span>
                <br />
                <span className="text-clinic-muted">
                  Freed for others and you won't be billed. Available until {formatDeadline(booking.start_time, CANCELLATION_WINDOW_HOURS)}.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-card border border-clinic-primary-lighter cursor-pointer">
              <input
                type="radio"
                name="release_room"
                checked={!releaseRoom}
                onChange={() => setReleaseRoom(false)}
                className="mt-1 accent-clinic-primary"
              />
              <span className="text-sm text-clinic-dark">
                <span className="font-medium">Keep room time</span>
                <br />
                <span className="text-clinic-muted">
                  You'll still be billed. Use the space yourself, or simply don't use it.
                </span>
              </span>
            </label>
          </fieldset>
        )}

        {(!isSelf || hasGuestClient) && (
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">
              {isSelf ? 'Reason (optional, sent to your client)' : 'Reason (required, sent to the client)'}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Briefly explain why you're cancelling."
              error={!isSelf && reason.length > 0 && !reasonValid}
            />
            {!isSelf && reason.length > 0 && !reasonValid && (
              <p className="text-xs text-clinic-accent mt-1">Please enter at least 3 characters.</p>
            )}
          </div>
        )}

        {!isSelf && (
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">
              Internal note (optional, admin only)
            </label>
            <Textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              rows={2}
              placeholder="Not shown to the client."
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CancelBookingModal;
